/**
 * Future Home Plan Foundation (FHPF) - Frontend API Service Client
 * Connects securely to the authoritative cPanel PHP backend.
 */

import { auth } from '../firebase/config';
import { globalCircuitBreaker } from '../utils/circuitBreaker';

export interface SubmitContributionPayload {
  month: string; // YYYY-MM
  amount: number; // 5000
  proofFile: File;
  idempotencyKey: string;
}

/**
 * Submits a payment proof to the PHP backend with Firebase ID token and Idempotency Key.
 */
export async function submitContributionApi(payload: SubmitContributionPayload): Promise<any> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User is not signed in.');
  }

  // Get fresh Firebase ID token
  const idToken = await user.getIdToken();

  const formData = new FormData();
  formData.append('month', payload.month);
  formData.append('amount', payload.amount.toString());
  formData.append('proof', payload.proofFile);

  const activeUrl = globalCircuitBreaker.getActiveUrl();
  try {
    const response = await fetch(`${activeUrl}/contributions/submit`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Idempotency-Key': payload.idempotencyKey,
      },
      body: formData,
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error?.message || `Submission failed with HTTP ${response.status}`);
    }

    globalCircuitBreaker.recordSuccess();
    return data;
  } catch (err: any) {
    globalCircuitBreaker.recordFailure(err);
    throw err;
  }
}

/**
 * Checks system health of the backend.
 */
export async function checkBackendHealth(): Promise<{ status: string; serverTimeDhaka: string }> {
  const activeUrl = globalCircuitBreaker.getActiveUrl();
  try {
    const response = await fetch(`${activeUrl}/health`);
    if (!response.ok) {
      throw new Error('Backend unhealthy');
    }
    const data = await response.json();
    globalCircuitBreaker.recordSuccess();
    return {
      status: data.status,
      serverTimeDhaka: data.server_time_dhaka,
    };
  } catch (err: any) {
    globalCircuitBreaker.recordFailure(err);
    throw err;
  }
}

/**
 * Reusable authorized API client for the PHP 8.3 backend with circuit breaker protection.
 */
export const apiClient = {
  async get(endpoint: string): Promise<any> {
    const user = auth.currentUser;
    const token = user ? await user.getIdToken() : null;
    const activeUrl = globalCircuitBreaker.getActiveUrl();

    try {
      const res = await fetch(`${activeUrl}${endpoint}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error?.message || `Request failed with HTTP ${res.status}`);
      globalCircuitBreaker.recordSuccess();
      return data;
    } catch (err: any) {
      globalCircuitBreaker.recordFailure(err);
      throw err;
    }
  },

  async post(endpoint: string, body?: any): Promise<any> {
    const user = auth.currentUser;
    const token = user ? await user.getIdToken() : null;
    const activeUrl = globalCircuitBreaker.getActiveUrl();

    try {
      const res = await fetch(`${activeUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error?.message || `Request failed with HTTP ${res.status}`);
      globalCircuitBreaker.recordSuccess();
      return data;
    } catch (err: any) {
      globalCircuitBreaker.recordFailure(err);
      throw err;
    }
  },
};
